import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import UserAddForm from './UserAddForm';

const UserAddPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, userLevel: currentUserLevel, permissions, loading: authLoading } = useAdminAuth();

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 권한 체크 - 인증이 완료된 후에만 확인
  const hasPermission = useMemo(() => {
    if (authLoading) return null; // 인증 중에는 null 반환
    if (!currentUser || !currentUserLevel) return false;
    // 최고관리자이거나 사용자 관리 권한이 있는 경우
    return currentUserLevel === 1 || permissions?.users;
  }, [currentUser, currentUserLevel, permissions, authLoading]);

  // 통합된 로딩 상태 - 인증 중일 때
  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              권한을 확인하는 중...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 없는 경우
  if (hasPermission === false) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] mb-2">
                    접근 권한 없음
                  </h2>
                  <p className="text-yellow-700 text-lg font-medium font-['Pretendard'] mb-4">
                    새로운 사용자를 추가할 권한이 없습니다.
                  </p>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 transition-colors"
                  >
                    목록으로 돌아가기
                  </button>
                </div>
                <svg className="w-12 h-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 1112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 확인되지 않은 경우
  if (hasPermission !== true) {
    return null;
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              새로운 사용자 추가
            </div>
        <button
          onClick={() => navigate('/admin/users')}
              className="w-[140px] h-11 rounded-sm border border-gray-300 text-[#6b7684] text-base font-medium font-['Pretendard'] hover:bg-gray-50 transition-colors"
        >
          목록으로 돌아가기
        </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
      </div>
      
        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
          새로운 사용자를 시스템에 추가합니다. 모든 필수 정보를 입력해주세요.
        </p>
          </div>
      </div>
      
        {/* 사용자 추가 폼 */}
        <UserAddForm currentUserLevel={currentUserLevel} />

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default UserAddPage;